import React, { useState } from 'react';
import usePortalUser from '../../auth/usePortalUser';
import ChangePasswordForm from '../../components/ChangePasswordForm';
import './Portal.css';
import './ParentPortal.css';

/* ─── Mock data ─────────────────────────────────────────────────────────────
   TODO: replace with GET /api/children?parentId=<id> once the portal API is
   wired up. Each child carries their own sessions and certificates arrays.
   ─────────────────────────────────────────────────────────────────────────── */

const MOCK_CHILDREN = [
  {
    id: 'child-1',
    name: 'Lily Smith',
    dob: '2021-03-14',
    // TODO: replace with GET /api/sessions?childId=child-1
    sessions: [
      {
        id: 's1',
        date: '2026-04-15',
        time: '10:00 AM',
        service: 'Object Identification: Foundational',
        consultant: 'Dr. Amara Reeves',
        status: 'confirmed',
      },
      {
        id: 's2',
        date: '2026-04-22',
        time: '2:00 PM',
        service: 'Mathematics: Advanced',
        consultant: 'Ms. Fatima Okafor',
        status: 'pending',
      },
    ],
    // TODO: replace with GET /api/certificates?childId=child-1
    certificates: [
      {
        id: 'c1',
        service: 'Speech: Storytelling & Understanding',
        earnedDate: '2026-02-10',
        program: 'SKIES Foundational',
      },
      {
        id: 'c2',
        service: 'Object Identification: Advanced',
        earnedDate: '2026-01-05',
        program: 'SKIES Advanced',
      },
    ],
  },
  {
    id: 'child-2',
    name: 'Marcus Smith',
    dob: '2023-08-02',
    sessions: [
      {
        id: 's3',
        date: '2026-04-18',
        time: '11:00 AM',
        service: 'Speech: Sounds & Early Reading',
        consultant: 'Dr. Amara Reeves',
        status: 'confirmed',
      },
    ],
    certificates: [],
  },
];

/* ─── Inline SVG icons ─── */
const HomeIcon     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const CalendarIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const AwardIcon    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>;
const SignOutIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>;
const PlusIcon     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const LockIcon     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;

const NAV_ITEMS = [
  { id: 'overview',     label: 'Overview',     icon: <HomeIcon /> },
  { id: 'sessions',     label: 'Sessions',     icon: <CalendarIcon /> },
  { id: 'certificates', label: 'Certificates', icon: <AwardIcon /> },
  { id: 'account',      label: 'Account',      icon: <LockIcon /> },
];

/* ─── Shared micro-components ─── */

function StatCard({ icon, iconClass, value, label }) {
  return (
    <div className="portal-stat-card">
      <div className={`psc-icon ${iconClass}`}>{icon}</div>
      <div className="psc-value">{value}</div>
      <div className="psc-label">{label}</div>
    </div>
  );
}

function PortalPanel({ title, action, onAction, children }) {
  return (
    <div className="portal-panel">
      <div className="portal-panel-header">
        <span className="portal-panel-title">{title}</span>
        {action && (
          <button className="portal-panel-action" onClick={onAction}>{action}</button>
        )}
      </div>
      {children}
    </div>
  );
}

function PortalEmptyState({ title, message, icon }) {
  return (
    <div className="portal-empty-state">
      {icon && <div className="pes-icon">{icon}</div>}
      <p className="pes-title">{title}</p>
      {message && <p className="pes-text">{message}</p>}
    </div>
  );
}

function StatusPill({ status }) {
  return <span className={`pill pill-${status}`}>{status.charAt(0).toUpperCase() + status.slice(1)}</span>;
}

/* ─── Child switcher tab bar ─── */
function ChildSwitcher({ children, activeId, onSwitch }) {
  return (
    <div className="child-switcher">
      {children.map(child => (
        <button
          key={child.id}
          className={`child-tab ${activeId === child.id ? 'active' : ''}`}
          onClick={() => onSwitch(child.id)}
        >
          <span className="child-tab-avatar">{child.name.charAt(0)}</span>
          {child.name}
        </button>
      ))}
    </div>
  );
}

/* ─── Overview view ─── */
function OverviewView({ child }) {
  const upcomingCount = child.sessions.length;
  const certCount     = child.certificates.length;

  const ageYears = (() => {
    const dob  = new Date(child.dob);
    const today = new Date();
    const age   = today.getFullYear() - dob.getFullYear();
    const m     = today.getMonth() - dob.getMonth();
    return m < 0 || (m === 0 && today.getDate() < dob.getDate()) ? age - 1 : age;
  })();

  return (
    <>
      <div className="child-summary-card">
        <div className="csc-avatar">{child.name.charAt(0)}</div>
        <div className="csc-info">
          <h2 className="csc-name">{child.name}</h2>
          <p className="csc-age">Age {ageYears} · DOB {child.dob}</p>
        </div>
        <a href="/book" className="btn btn-sm btn-aero" style={{ marginLeft: 'auto' }}>
          <PlusIcon /> Book Session
        </a>
      </div>

      <div className="portal-stat-grid">
        <StatCard
          icon={<AwardIcon />}
          iconClass="psc-icon-gold"
          value={certCount}
          label="Certificates Earned"
        />
        <StatCard
          icon={<CalendarIcon />}
          iconClass="psc-icon-sky"
          value={upcomingCount}
          label="Upcoming Sessions"
        />
      </div>

      <PortalPanel title="Upcoming Sessions">
        {child.sessions.length === 0
          ? <PortalEmptyState
              icon={<CalendarIcon />}
              title="No sessions scheduled"
              message="Book a session to get started."
            />
          : (
            <div className="portal-table-wrap">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Service</th>
                    <th>Consultant</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {child.sessions.map(s => (
                    <tr key={s.id}>
                      <td className="td-primary">{s.date}</td>
                      <td>{s.time}</td>
                      <td>{s.service}</td>
                      <td>{s.consultant}</td>
                      <td><StatusPill status={s.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </PortalPanel>

      <PortalPanel title="Recent Certificates">
        {child.certificates.length === 0
          ? <PortalEmptyState
              icon={<AwardIcon />}
              title="No certificates yet"
              message="Complete a session to earn your first certificate."
            />
          : (
            <div className="cert-list">
              {child.certificates.slice(0, 3).map(cert => (
                <div key={cert.id} className="cert-list-item">
                  <div className="cli-icon"><AwardIcon /></div>
                  <div className="cli-info">
                    <p className="cli-service">{cert.service}</p>
                    <p className="cli-date">Earned {cert.earnedDate} · <span className="badge badge-gold">{cert.program}</span></p>
                  </div>
                  <button className="btn btn-sm btn-dark" disabled style={{ opacity: 0.5, flexShrink: 0 }}>
                    Download PDF
                  </button>
                </div>
              ))}
            </div>
          )
        }
      </PortalPanel>
    </>
  );
}

/* ─── Sessions view ─── */
function SessionsView({ child }) {
  return (
    <PortalPanel title={`All Sessions: ${child.name}`}>
      {child.sessions.length === 0
        ? <PortalEmptyState
            icon={<CalendarIcon />}
            title="No sessions scheduled"
            message="Book a session to get started."
          />
        : (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Service</th>
                  <th>Consultant</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {child.sessions.map(s => (
                  <tr key={s.id}>
                    <td className="td-primary">{s.date}</td>
                    <td>{s.time}</td>
                    <td>{s.service}</td>
                    <td>{s.consultant}</td>
                    <td><StatusPill status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
    </PortalPanel>
  );
}

/* ─── Certificates view ─── */
function CertificatesView({ child }) {
  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', color: 'var(--midnight)' }}>
          Certificates: {child.name}
        </h2>
        <p style={{ fontSize: '0.875rem', color: 'var(--gray-400)', marginTop: '4px' }}>
          Official SKIES &amp; AERO program certifications earned during sessions.
        </p>
      </div>

      {child.certificates.length === 0
        ? (
          <div className="portal-panel">
            <PortalEmptyState
              icon={<AwardIcon />}
              title="No certificates yet"
              message="Complete a session to earn your first official certificate."
            />
          </div>
        )
        : (
          <div className="cert-grid">
            {child.certificates.map(cert => (
              <div key={cert.id} className="cert-card">
                <div className="cert-card-icon"><AwardIcon /></div>
                <p className="cert-card-service">{cert.service}</p>
                <p className="cert-card-date">Earned {cert.earnedDate}</p>
                <div className="cert-card-footer">
                  <span className="badge badge-gold">{cert.program}</span>
                  {/* TODO: wire to real PDF generation endpoint */}
                  <button className="btn btn-sm btn-dark" disabled style={{ opacity: 0.5 }}>
                    Download PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </>
  );
}

/* ─── Main component ─── */
export default function ParentPortal() {
  const user = usePortalUser();
  const [activeView,    setActiveView]    = useState('overview');
  const [activeChildId, setActiveChildId] = useState(MOCK_CHILDREN[0].id);

  const child = MOCK_CHILDREN.find(c => c.id === activeChildId);

  const activeNavLabel = NAV_ITEMS.find(n => n.id === activeView)?.label ?? 'Overview';

  return (
    <div className="portal-shell">
      {/* ── Sidebar ── */}
      <aside className="portal-sidebar">
        {/* Logo — .sidebar-logo-circle is the white ring, .sidebar-logo-img is the mark (sized independently) */}
        <div className="sidebar-logo">
          <span className="sidebar-logo-circle">
            <img src="/stratos-logo.png" alt="Stratos" className="sidebar-logo-img" />
          </span>
          <span className="sidebar-logo-text">
            <span style={{ color: 'var(--gold)', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Parent Portal</span>
          </span>
        </div>

        <nav className="sidebar-nav" aria-label="Parent portal navigation">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${activeView === item.id ? 'active' : ''}`}
              onClick={() => setActiveView(item.id)}
              aria-current={activeView === item.id ? 'page' : undefined}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-user-footer">
          <div className="sidebar-user-row">
            <div className="sidebar-avatar">{user.name.charAt(0)}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user.name}</div>
              <div className="sidebar-user-role">{user.role}</div>
            </div>
          </div>
          <button className="sidebar-signout" onClick={user.signOut}><SignOutIcon /> Sign Out</button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="portal-main">
        <div className="portal-topbar">
          <span className="portal-topbar-title">{activeNavLabel}</span>
          <div className="portal-topbar-right">
            <a href="/book" className="btn btn-sm btn-primary">
              <PlusIcon /> Book a Session
            </a>
          </div>
        </div>

        <div className="portal-body">
          {/* Child switcher — visible on all views */}
          <ChildSwitcher
            children={MOCK_CHILDREN}
            activeId={activeChildId}
            onSwitch={setActiveChildId}
          />

          {activeView === 'overview'     && <OverviewView     child={child} />}
          {activeView === 'sessions'     && <SessionsView     child={child} />}
          {activeView === 'certificates' && <CertificatesView child={child} />}
          {activeView === 'account'      && <ChangePasswordForm />}
        </div>
      </div>
    </div>
  );
}
